import _ from "lodash";
import { Info, Plus, Trash2, X } from "lucide-react";
import { Dialog as RadixDialog } from "radix-ui";
import React, { useState } from "react";

import { Button } from "@/design-system/components/Button/Button";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { DialogActionsWrapper } from "@/design-system/components/Dialog/Dialog";
import { Input } from "@/design-system/components/Input/Input";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { PropertySensitivityLevels, SensitivityLevel } from "@/types/global";
import { isAccountInHIPAAModeV2 } from "@/util";

// The list of built-in props to offer for the allowlist.
export const allowlistBuiltInProps: Record<string, { beautifulName: string }> =
  {
    $title: { beautifulName: "Page Title" },
    $current_url: { beautifulName: "URL" },
    utm_source: { beautifulName: "UTM Source" },
    utm_content: { beautifulName: "UTM Content" },
    utm_term: { beautifulName: "UTM Term" },
    utm_campaign: { beautifulName: "UTM Campaign" },
    utm_medium: { beautifulName: "UTM Medium" },
    $query_params: { beautifulName: "Other Query Parameters" },
    $pathname: { beautifulName: "Pathname" },
    $ip: { beautifulName: "IP Address" },
    $user_agent: { beautifulName: "User Agent" },
    $initial_referrer: { beautifulName: "Initial Referrer" },
    $initial_referring_domain: { beautifulName: "Initial Referring Domain" },
    $referrer: { beautifulName: "Referrer" },
    $referring_domain: { beautifulName: "Referring Domain" },
    $gclid: { beautifulName: "Google Ads Click ID (GCLID)" },
    $gbraid: { beautifulName: "Google Ads GBRAID" },
    $wbraid: { beautifulName: "Google Ads WBRAID" },
    $dclid: { beautifulName: "Google Marketing Platform Click ID (DCLID)" },
    $edimpid: { beautifulName: "Google Marketing Platform Impression ID" },
    $gclsrc: { beautifulName: "Google Click Source (GCLSRC)" },
    $auiddc: { beautifulName: "DoubleClick Cookie ID (AUIDDC)" },
    $fbclid: { beautifulName: "Facebook Ad Click ID" },
    $msclkid: { beautifulName: "Bing Ad Click ID" },
    $twclid: { beautifulName: "Twitter Ad Click ID" },
    $ttclid: { beautifulName: "TikTok Ad Click ID" },
    $irclickid: { beautifulName: "impact.com Click ID" },
    $li_fat_id: { beautifulName: "LinkedIn Ads Click ID (LI_FAT_ID)" },
    $rdt_cid: { beautifulName: "Reddit Ads Click ID (rdt_cid)" },
    $epik: { beautifulName: "Pinterest Ads Click ID (EPIK)" },
    $cntr_auctionId: { beautifulName: "Basis Ad Click ID (cntr_auctionId)" },
    $viant_clid: { beautifulName: "Viant Click ID (viant_clid)" },
    $viant_impid: { beautifulName: "Viant View Impression ID (viant_impid)" },
    $idfv: { beautifulName: "IDFV (Identifier for Vendor)" },
    $idfa: { beautifulName: "IDFA (Identifier for Advertisers)" },
    $att_status: { beautifulName: "App Tracking Transparency Status" },
    $gaid: { beautifulName: "Google Advertising ID (GAID)" },
    $install_referrer: { beautifulName: "Install Referrer" },
  };

const allowlistBuiltInPropDisplayOrder: string[] = [
  "$title",
  "$current_url",
  "utm_source",
  "utm_content",
  "utm_term",
  "utm_campaign",
  "utm_medium",
  "$query_params",
  "$pathname",
  "$ip",
  "$user_agent",
  "$initial_referrer",
  "$initial_referring_domain",
  "$referrer",
  "$referring_domain",
  "$gclid",
  "$gbraid",
  "$wbraid",
  "$dclid",
  "$edimpid",
  "$gclsrc",
  "$auiddc",
  "$fbclid",
  "$msclkid",
  "$twclid",
  "$ttclid",
  "$irclickid",
  "$li_fat_id",
  "$rdt_cid",
  "$epik",
  "$cntr_auctionId",
  "$viant_clid",
  "$viant_impid",
  "$idfv",
  "$idfa",
  "$att_status",
  "$gaid",
  "$install_referrer",
];

function isBuiltInProp(propName: string): boolean {
  return !!allowlistBuiltInProps[propName];
}

interface PHIAllowListEditModalProps {
  allowListType: string;
  propertySensitivityLevels: PropertySensitivityLevels;
  open: boolean;
  onSave: (newSensitivityLevels: PropertySensitivityLevels) => Promise<void>;
  onClose: () => void;
}

export const PHIAllowListEditModal: React.FC<PHIAllowListEditModalProps> = ({
  allowListType,
  propertySensitivityLevels,
  open,
  onSave,
  onClose,
}) => {
  const accountMetadata = useAccountMetadata();

  let initialBuiltInPropNames: string[] = [];
  let initialCustomPropNames = Object.keys(propertySensitivityLevels);
  if (isAccountInHIPAAModeV2(accountMetadata)) {
    [initialBuiltInPropNames, initialCustomPropNames] = _.partition(
      Object.keys(propertySensitivityLevels),
      isBuiltInProp,
    );
  }
  const [builtInPropsState, setBuiltInPropsState] = useState(
    _.pick(propertySensitivityLevels, initialBuiltInPropNames),
  );
  const initialCustomProps: [string, SensitivityLevel][] =
    Object.keys(propertySensitivityLevels).length !== 0
      ? Object.entries(
          _.pick(propertySensitivityLevels, initialCustomPropNames),
        )
      : [["", SensitivityLevel.NotSensitive]];
  const [customPropsState, setCustomPropsState] = useState(initialCustomProps);

  const onDeleteCustomProp = (index: number) => {
    const newProps = customPropsState.slice();
    newProps.splice(index, 1);
    setCustomPropsState(newProps);
  };

  const onChangeCustomProp = (
    index: number,
    newProp: string,
    newSensitivityLevel: SensitivityLevel,
  ) => {
    const newProps = customPropsState.slice();
    newProps[index] = [newProp, newSensitivityLevel];
    setCustomPropsState(newProps);
  };

  const setBuiltinPropChecked = (propName: string, checked: boolean) => {
    setBuiltInPropsState((state) => {
      const next = { ...state };
      if (checked) {
        next[propName] = SensitivityLevel.NotSensitive;
      } else {
        delete next[propName];
      }
      return next;
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onClose();
    }
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={handleOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn z-overlay fixed inset-0 flex items-center justify-center bg-black/50 p-4 sm:p-8">
          <RadixDialog.Content className="data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn bg-surface-default flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg shadow-lg outline-none">
            <div className="border-borderSubtle flex flex-none items-center justify-between border-b px-6 py-4">
              <RadixDialog.Title className="text-textDefault text-lg font-semibold">
                Allow list: {allowListType}
              </RadixDialog.Title>
              <RadixDialog.Close
                type="button"
                className="text-textSubtle hover:text-textDefault cursor-pointer rounded p-1 transition-colors"
                aria-label="Close"
              >
                <X className="size-5" />
              </RadixDialog.Close>
            </div>

            <RadixDialog.Description className="sr-only">
              Edit HIPAA allowlisted properties for {allowListType}.
            </RadixDialog.Description>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="space-y-8">
                {isAccountInHIPAAModeV2(accountMetadata) && (
                  <section>
                    <h2 className="text-textDefault mb-3 text-base font-semibold">
                      Built-in properties
                    </h2>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                      {allowlistBuiltInPropDisplayOrder.map((propName) => (
                        <label
                          key={propName}
                          className="hover:bg-surface-hover-subtle flex cursor-pointer items-start gap-2 rounded-md py-1 pr-1 text-sm"
                        >
                          <Checkbox
                            className="mt-0.5 shrink-0"
                            checked={!!builtInPropsState[propName]}
                            onCheckedChange={(checked) => {
                              if (checked === "indeterminate") {
                                return;
                              }
                              setBuiltinPropChecked(propName, checked);
                            }}
                          />
                          <span className="text-textDefault leading-snug">
                            {allowlistBuiltInProps[propName].beautifulName}
                          </span>
                        </label>
                      ))}
                    </div>
                  </section>
                )}

                <section>
                  <h2 className="text-textDefault mb-2 text-base font-semibold">
                    Custom properties
                  </h2>
                  <p className="text-textSubtle mb-4 text-sm">
                    Add property keys to allowlist. Optional masking hashes
                    values before they reach destinations.
                  </p>

                  <div className="border-borderSubtle bg-surface-default overflow-hidden rounded-lg border shadow-sm">
                    <ul className="divide-dividerDefault divide-y">
                      {customPropsState.map(
                        ([prop, sensitivityLevel], index) => {
                          const isHashed =
                            sensitivityLevel ===
                            SensitivityLevel.NotSensitiveIfHashed;
                          return (
                            <li
                              className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-4"
                              key={index}
                            >
                              <Tooltip content="Remove this property from the allow list">
                                <button
                                  type="button"
                                  aria-label="Remove this property from the allow list"
                                  className="border-borderDefault text-textSubtle hover:text-textDefault bg-surface-default flex size-10 shrink-0 cursor-pointer items-center justify-center self-start rounded-md border shadow-sm transition-colors hover:border-gray-400 sm:self-center"
                                  onClick={() => onDeleteCustomProp(index)}
                                >
                                  <Trash2 className="size-4" aria-hidden />
                                </button>
                              </Tooltip>

                              <div className="min-w-0 flex-1 sm:pt-0">
                                <Input
                                  label="Property name"
                                  placeholder="allowlisted_property"
                                  autoComplete="off"
                                  name="Allowlisted Property"
                                  value={prop || ""}
                                  onChange={(event) =>
                                    onChangeCustomProp(
                                      index,
                                      event.target.value,
                                      sensitivityLevel,
                                    )
                                  }
                                  className="fs-exclude font-mono text-sm"
                                />
                              </div>

                              <div className="border-dividerDefault flex shrink-0 items-center gap-2 border-t pt-3 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-4">
                                <div className="text-textDefault flex items-center gap-2 text-sm whitespace-nowrap">
                                  <Checkbox
                                    id={`phi-allowlist-masked-${index}`}
                                    checked={isHashed}
                                    onCheckedChange={(checked) => {
                                      if (checked === "indeterminate") {
                                        return;
                                      }
                                      const newSensitivityLevel = checked
                                        ? SensitivityLevel.NotSensitiveIfHashed
                                        : SensitivityLevel.NotSensitive;
                                      onChangeCustomProp(
                                        index,
                                        prop,
                                        newSensitivityLevel,
                                      );
                                    }}
                                  />
                                  <label
                                    htmlFor={`phi-allowlist-masked-${index}`}
                                    className="cursor-pointer select-none"
                                  >
                                    Masked
                                  </label>
                                  <Tooltip
                                    content={
                                      <span className="text-sm">
                                        When enabled, Freshpaint will apply a
                                        cryptographic hash to the property
                                        before sending to the destination.
                                      </span>
                                    }
                                  >
                                    <button
                                      type="button"
                                      className="text-textSubtle hover:text-textDefault cursor-pointer rounded p-0.5"
                                      aria-label="About masking"
                                    >
                                      <Info className="size-4" />
                                    </button>
                                  </Tooltip>
                                </div>
                              </div>
                            </li>
                          );
                        },
                      )}
                    </ul>
                  </div>

                  <Button
                    variant="outline"
                    type="button"
                    className="mt-4"
                    onClick={() =>
                      setCustomPropsState([
                        ...customPropsState,
                        ["", SensitivityLevel.NotSensitive],
                      ])
                    }
                    leadingIcon={<Plus className="size-4" />}
                  >
                    Add property
                  </Button>

                  <div className="text-textSubtle bg-surface-selected border-interactive-selected/20 mt-6 rounded-md border px-4 py-3 text-sm leading-relaxed">
                    Learn more about HIPAA Mode and ID masking{" "}
                    <a
                      href="https://documentation.freshpaint.io/readme/hipaa-mode#id-masking"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-textActionSelected cursor-pointer font-medium hover:underline"
                    >
                      in our docs
                    </a>
                    .
                  </div>
                </section>
              </div>
            </div>

            <div className="border-borderSubtle flex-none border-t px-6 py-4">
              <DialogActionsWrapper>
                <Button variant="ghost" type="button" onClick={onClose}>
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={() =>
                    onSave(
                      _.merge(
                        {},
                        builtInPropsState,
                        Object.fromEntries(customPropsState),
                      ),
                    )
                  }
                >
                  Save
                </Button>
              </DialogActionsWrapper>
            </div>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};
